<template>
    <div class="kling-model-page">
        <!-- Video Generator Section -->
        <section ref="generatorSection" class="video-generator-section">
            <div class="container">
                <div class="generator-container">
                    <!-- Generator Header -->
                    <div class="generator-header">
                        <div class="generator-title">
                            <h1>{{ t('kling.generatorTitle') }}</h1>
                            <p class="generator-description" v-html="t('kling.generatorDescription')"></p>
                            <div class="rainbow-bar"></div>
                        </div>
                    </div>

                    <!-- Generator Interface -->
                    <VideoGenerator model-name="Kling" :tabs="tabs" redirect-url="/ai-video" @generate="handleGenerate"
                        model-key="KLING_V2_5_TURBO">
                        <template #model-logo>
                            <div class="kling-logo">K</div>
                        </template>
                    </VideoGenerator>
                </div>
            </div>
        </section>

        <!-- Core Features Section -->
        <section class="core-features-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('kling.coreFeaturesTitle') }}
                    </h2>
                </div>

                <div class="features-showcase-container">
                    <!-- Feature 1: 精细的角色表情 -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{
                                        t('kling.refinedFacialExpressionTitle')
                                    }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t(
                                            'kling.refinedFacialExpressionDescription'
                                        )
                                    }}
                                </p>
                            </div>
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.refinedFacialExpression"
                                    :poster="getResourceThumbnail(demoVideos.refinedFacialExpression)"
                                    :title="t('kling.refinedFacialExpressionTitle')"
                                    :description="t('kling.refinedFacialExpressionDescription')"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Feature 2: 改进的动态效果 -->
                    <div class="feature-showcase reverse">
                        <div class="feature-content">
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.improvedMotion"
                                    :poster="getResourceThumbnail(demoVideos.improvedMotion)"
                                    :title="t('kling.improvedMotionTitle')"
                                    :description="t('kling.improvedMotionDescription')"
                                />
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('kling.improvedMotionTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{ t('kling.improvedMotionDescription') }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Feature 3: 细致的视觉构图 -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{
                                        t(
                                            'kling.detailedVisualCompositionTitle'
                                        )
                                    }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t(
                                            'kling.detailedVisualCompositionDescription'
                                        )
                                    }}
                                </p>
                            </div>
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.detailedVisualComposition"
                                    :poster="getResourceThumbnail(demoVideos.detailedVisualComposition)"
                                    :title="t('kling.detailedVisualCompositionTitle')"
                                    :description="t('kling.detailedVisualCompositionDescription')"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Feature 4: 升级的风格一致性 -->
                    <div class="feature-showcase reverse">
                        <div class="feature-content">
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.enhancedStyleConsistency"
                                    :poster="getResourceThumbnail(demoVideos.enhancedStyleConsistency)"
                                    :title="t('kling.enhancedStyleConsistencyTitle')"
                                    :description="t('kling.enhancedStyleConsistencyDescription')"
                                />
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{
                                        t('kling.enhancedStyleConsistencyTitle')
                                    }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t(
                                            'kling.enhancedStyleConsistencyDescription'
                                        )
                                    }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Feature 5: 更强的镜头控制 -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('kling.strongerLensControlTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t(
                                            'kling.strongerLensControlDescription'
                                        )
                                    }}
                                </p>
                            </div>
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.strongerLensControl"
                                    :poster="getResourceThumbnail(demoVideos.strongerLensControl)"
                                    :title="t('kling.strongerLensControlTitle')"
                                    :description="t('kling.strongerLensControlDescription')"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Feature 6: 增强的语义理解 -->
                    <div class="feature-showcase reverse">
                        <div class="feature-content">
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.enhancedSemanticUnderstanding"
                                    :poster="getResourceThumbnail(demoVideos.enhancedSemanticUnderstanding)"
                                    :title="t('kling.enhancedSemanticUnderstandingTitle')"
                                    :description="t('kling.enhancedSemanticUnderstandingDescription')"
                                />
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{
                                        t(
                                            'kling.enhancedSemanticUnderstandingTitle'
                                        )
                                    }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t(
                                            'kling.enhancedSemanticUnderstandingDescription'
                                        )
                                    }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Feature 7: 优化的视频处理 -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{
                                        t('kling.optimizedVideoProcessingTitle')
                                    }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t(
                                            'kling.optimizedVideoProcessingDescription'
                                        )
                                    }}
                                </p>
                            </div>
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.optimizedVideoProcessing"
                                    :poster="getResourceThumbnail(demoVideos.optimizedVideoProcessing)"
                                    :title="t('kling.optimizedVideoProcessingTitle')"
                                    :description="t('kling.optimizedVideoProcessingDescription')"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- How to Use Section -->
        <section class="how-to-use-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('kling.howToUseTitle') }}
                    </h2>
                </div>

                <ol class="steps-container">
                    <li class="step-item">
                        <div class="step-number">1</div>
                        <div class="step-content">
                            <h3 class="step-title">
                                {{ t('kling.step1Title') }}
                            </h3>
                            <p class="step-description" v-html="t('kling.step1Description')"></p>
                        </div>
                    </li>

                    <li class="step-item">
                        <div class="step-number">2</div>
                        <div class="step-content">
                            <h3 class="step-title">
                                {{ t('kling.step2Title') }}
                            </h3>
                            <p class="step-description">
                                {{ t('kling.step2Description') }}
                            </p>
                        </div>
                    </li>

                    <li class="step-item">
                        <div class="step-number">3</div>
                        <div class="step-content">
                            <h3 class="step-title">
                                {{ t('kling.step3Title') }}
                            </h3>
                            <p class="step-description">
                                {{ t('kling.step3Description') }}
                            </p>
                        </div>
                    </li>
                </ol>
            </div>
        </section>

        <!-- YouTube Videos Section -->
        <YouTubeCarouselSection :title="t('kling.youtubeTitle')" :videos="youtubeVideos" />

        <!-- X Posts Section -->
        <TwitterPosts :title="t('kling.xPostsTitle')" :tweet-urls="twitterTweetUrls" />

        <!-- Explore Other Models Section -->
        <section class="explore-models-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('kling.exploreModelsTitle') }}
                    </h2>
                </div>

                <div class="models-grid">
                    <NuxtLink to="/video/veo/veo-3-1" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/3ed8c9e1a1ec3b37133c608c292821cb.png"
                                alt="Google Veo 3.1 AI video model icon" loading="lazy" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('kling.veo31Title') }}
                        </h3>
                    </NuxtLink>

                    <NuxtLink to="/video/sora/sora-2" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/7ac1a1457b1e788829a2da1d178879bb.png"
                                alt="OpenAI Sora 2 AI video model icon" loading="lazy" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('kling.sora2Title') }}
                        </h3>
                    </NuxtLink>

                    <NuxtLink to="/video/hailuo-23" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/2b9d9924fb8b25932b804c7fe99d792d.png"
                                alt="Hailuo 2.3 AI video model icon" loading="lazy" />
                        </div>
                        <h3 class="model-card-title">Hailuo 2.3</h3>
                    </NuxtLink>
                </div>
            </div>
        </section>

        <!-- FAQ Section -->
        <section class="faq-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">{{ t('kling.faqTitle') }}</h2>
                </div>

                <div class="faq-list">
                    <div v-for="(faq, index) in faqData" :key="index" class="faq-item"
                        :class="{ active: activeFaqIndex === index }">
                        <div class="faq-question" @click="toggleFaq(index)">
                            <!-- <span class="faq-icon">></span> -->
                            <span class="faq-text">{{ faq.question }}</span>
                        </div>
                        <div class="faq-answer">
                            <p>{{ faq.answer }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- CTA Section -->
        <section class="cta-section">
            <div class="container">
                <div class="cta-content">
                    <!-- 微鲤AI Logo -->
                    <div class="cta-logo">
                        <div class="logo-icon logo-image">
                            <img src="https://static.weryai.com/growth/image/4608e0a35205ff929dba2ea3c51f6e62.webp"
                                alt="微鲤AI logo" class="logo-image" loading="lazy" />
                        </div>
                    </div>

                    <!-- Main Content -->
                    <div class="cta-header">
                        <h2 class="cta-title">{{ t('kling.ctaTitle') }}</h2>
                        <p class="cta-description">
                            {{ t('kling.ctaDescription') }}
                        </p>
                    </div>

                    <!-- CTA Button -->
                    <div class="cta-actions">
                        <button @click="scrollToGenerator" class="cta-primary-btn">
                            {{ t('kling.ctaButton') }}
                        </button>

                        <p class="cta-disclaimer">
                            {{ t('kling.ctaDisclaimer') }}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useHead, useRequestURL } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { useHowToSeo } from '~/composables/useHowToSeo'
import { useVideoSeo } from '~/composables/useVideoSeo'
import { analyticsEvent } from '~/utils/log-event-func'
import YouTubeCarouselSection from '~/components/model/video/YouTubeCarouselSection.vue'
import { getResourceThumbnail } from '~/utils/common'

const { t } = useI18n()
const requestUrl = useRequestURL()
const requestUrlHref = requestUrl.href

const demoVideos = {
    refinedFacialExpression:
        'https://static.weryai.com/growth/video/712f17ac19a688e61959d9f322a36f2b.mp4',
    improvedMotion:
        'https://static.weryai.com/growth/video/12b53e99ae6dc665940a67360fe1ea98.mp4',
    detailedVisualComposition:
        'https://static.weryai.com/growth/video/2119650472d7a2598f644175c8b2a018.mp4',
    enhancedStyleConsistency:
        'https://static.weryai.com/growth/video/2d8725e33aaff25be245fbd88493001f.mp4',
    strongerLensControl:
        'https://static.weryai.com/growth/video/acab44d4acdc3d94532c489ef9c985f0.mp4',
    enhancedSemanticUnderstanding:
        'https://static.weryai.com/growth/video/6d56b40c185791abc92160b089d084f1.mp4',
    optimizedVideoProcessing:
        'https://static.weryai.com/growth/video/cd87b6fbee00b13ecbb66f907e4d4a9b.mp4',
} as const

definePageMeta({
    layout: 'website',
})

useMultilingualSeo({ pageType: 'kling_ai' })

// BreadcrumbList SEO
useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: 'Home',
                            item: 'https://www.weryai.com.cn/',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('kling.generatorTitle'),
                            item: requestUrlHref,
                        },
                    ],
                })
            ),
        },
    ],
})

onMounted(() => {
    analyticsEvent('view_model_page_common', {
        model_name: 'kling_2_5_turbo',
        type: 'video',
    })
})

// Ref for generator section
const generatorSection = ref<HTMLElement | null>(null)

// Scroll to generator function
const scrollToGenerator = () => {
    if (generatorSection.value) {
        generatorSection.value.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
    }
}

// Structured Data for SEO
const structuredData = computed(() => ({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: t('kling.generatorTitle'),
    description: t('kling.pageDescription'),
    applicationCategory: 'MultimediaApplication',
    operatingSystem: 'Web',
    offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
    },
    provider: {
        '@type': 'Organization',
        name: '微鲤AI',
        url: 'https://www.weryai.com.cn',
    },
    featureList: [
        t('kling.refinedFacialExpressionTitle'),
        t('kling.improvedMotionTitle'),
        t('kling.detailedVisualCompositionTitle'),
        t('kling.strongerLensControlTitle'),
    ],
}))

useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: computed(() => JSON.stringify(structuredData.value)),
        },
    ],
})

// Tabs configuration
const tabs = computed(() => [
    { name: t('kling.tabImageToVideo'), external: false },
    { name: t('kling.tabTextToVideo'), external: false },
])

// Methods
const handleGenerate = (data: {
    type: 'image' | 'text'
    image?: File
    prompt?: string
}) => {
    // Handle generation logic here, such as sending to API
    // 组件内部已经处理了跳转逻辑
}

// YouTube Videos Data
const youtubeVideos = ref([
    {
        title: 'Kling AI Video 1',
        embedUrl: 'https://www.youtube.com/embed/ZqxP616nvIE',
    },
    {
        title: 'Kling AI Video 2',
        embedUrl: 'https://www.youtube.com/embed/1iwDFFR_LvY',
    },
    {
        title: 'Kling AI Video 3',
        embedUrl: 'https://www.youtube.com/embed/oJpNJ-SmU1A',
    },
    {
        title: 'Kling AI Video 4',
        embedUrl: 'https://www.youtube.com/embed/ZqxP616nvIE',
    },
])

// Video SEO - 注入 VideoObject 结构化数据
useVideoSeo(youtubeVideos.value)

// HowTo SEO - 注入结构化数据
const howToUseSteps = computed(() => [
    {
        title: t('kling.step1Title'),
        description: t('kling.step1Description'),
    },
    {
        title: t('kling.step2Title'),
        description: t('kling.step2Description'),
    },
    {
        title: t('kling.step3Title'),
        description: t('kling.step3Description'),
    },
])

useHowToSeo(
    computed(() => t('kling.howToUseTitle')),
    howToUseSteps
)

// Twitter Embedded Tweets URLs
const twitterTweetUrls = [
    'https://twitter.com/mikesharkai/status/1970636722401878064',
    'https://twitter.com/venturetwins/status/1970563820478439546',
    'https://twitter.com/umesh_ai/status/1970497680536150454',
    'https://twitter.com/aiIgnatius/status/1970458086130491491',
    'https://twitter.com/Gdgtify/status/1970459063105262015',
    'https://twitter.com/WuxiaRocks/status/1970452326688460961',
    'https://twitter.com/fAIkout/status/1970505756853334324',
    'https://twitter.com/StevieMac03/status/1970559778804908331',
    'https://twitter.com/pabloprompt/status/1970530533093581276',
    'https://twitter.com/eattheethos/status/1970617737832735040',
    'https://twitter.com/seirdotmk/status/1970513250266574869',
    'https://twitter.com/Gdgtify/status/1970502413762335126',
    'https://twitter.com/shedntcare_/status/1970478286099919322',
    'https://twitter.com/MiguelMaestroIA/status/1970488615521108101',
]

// FAQ Data
const faqData = computed(() => [
    {
        question: t('kling.faq1Question'),
        answer: t('kling.faq1Answer'),
    },
    {
        question: t('kling.faq2Question'),
        answer: t('kling.faq2Answer'),
    },
    {
        question: t('kling.faq3Question'),
        answer: t('kling.faq3Answer'),
    },
    {
        question: t('kling.faq4Question'),
        answer: t('kling.faq4Answer'),
    },
    {
        question: t('kling.faq5Question'),
        answer: t('kling.faq5Answer'),
    },
])

// FAQ Interactive Functions
const activeFaqIndex = ref<number | null>(null)

const toggleFaq = (index: number) => {
    if (activeFaqIndex.value === index) {
        activeFaqIndex.value = null
    } else {
        activeFaqIndex.value = index
    }
}
</script>

<style lang="less" scoped>
@import '~/assets/css/howToUse.css';
</style>
